import React from "react";
import { useDraggable } from "@dnd-kit/core";
import {
  CountBadge,
  NextAirDate,
  ShowBoxCurator,
  ShowBoxRoot,
  ShowBoxTitle,
  SubmittedCountBadge,
} from "./ScheduleBoard.styles";
import { DragData, ShowState } from "./types";

interface ShowBoxProps {
  id: string;
  title: string;
  subtitle?: string;
  // undefined => neutral styling (used for note-shows, which have no state).
  state?: ShowState;
  count?: number;
  // Submitted-playlist count, shown in green only when the show has no
  // archived playlists (count is 0/undefined).
  submittedCount?: number;
  // Compact "MM.DD" (or "—") next-air date; only set for stopping shows.
  nextAirDate?: string;
  // "White blocks" mode: render as a white block with black text.
  whiteBlocks?: boolean;
  data: DragData;
}

const ShowBox = ({
  id,
  title,
  subtitle,
  state,
  count,
  submittedCount,
  nextAirDate,
  whiteBlocks,
  data,
}: ShowBoxProps): React.JSX.Element => {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id,
    data,
  });

  const hasCount = typeof count === "number" && count > 0;
  // Fall back to the submitted count (green) only for shows with no archives.
  const showSubmitted =
    !hasCount && typeof submittedCount === "number" && submittedCount > 0;
  const tooltip = subtitle ? `${title} — ${subtitle}` : title;

  return (
    <ShowBoxRoot
      ref={setNodeRef}
      $dragging={isDragging}
      $state={state}
      $whiteBlocks={whiteBlocks}
      title={tooltip}
      {...listeners}
      {...attributes}
    >
      <ShowBoxTitle>
        {title}
        {hasCount ? <CountBadge>({count})</CountBadge> : null}
        {showSubmitted ? (
          <SubmittedCountBadge title="Submitted playlists">
            ({submittedCount})
          </SubmittedCountBadge>
        ) : null}
      </ShowBoxTitle>
      {nextAirDate ? (
        <NextAirDate title="Next air date (master schedule)">
          {nextAirDate}
        </NextAirDate>
      ) : null}
      {subtitle ? <ShowBoxCurator>{subtitle}</ShowBoxCurator> : null}
    </ShowBoxRoot>
  );
};

export default ShowBox;
